import { FC, useEffect, useState, useRef } from "react";
import { Box, Typography } from "@mui/material";

import { Colors } from "../../configs";
import { PosLayout } from "../../components";
import InfoSection from "../../components/pos/info-section/Info-section";
import ServicesSection from "../../components/pos/services-section/services-section";
import TopRightButton from "../../components/common/top-right-button/top-right-button";
import {
  useNavigate,
  useParams,
  useLocation,
  useBlocker,
  useSearchParams,
} from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { CartSliceAction } from "../../redux/slice/cartSlice";
import { CustomerSliceAction } from "../../redux/slice/customerSlice";
import { DiscountSliceAction } from "../../redux/slice/discountSlice";
import { discountFromSavedBill, editDiscountState } from "../../util/discount-calc";
import ConfirmDialog from "../../components/atoms/confirm-box/confirm-box";
import { IDraft } from "../../typings/interfaces/pos/draft";
import { formatPhoneNumber } from "../../util/common-functions";
import { useSnackbarContext } from "../../providers/SnackbarProvider";
import ExitConfirmationModal from "../../components/pos/info-section/cart/ExitConfirmationModal";
import { getCustomerByIdAction } from "../../redux/actions/customerAction";
import { ICustomerResponse, PosInfoSectionMode } from "../../typings";
import { ICartSession } from "../../typings/interfaces/pos/cart-session";
import {
  addSession,
  deleteDraftAction,
  getBillByIdAction,
  getBillItemIdListAction,
  getCashierDraftsAction,
  removeSession,
  saveDraftAction,
} from "../../redux/actions/cartAction";
import usePricing from "../../hooks/usePricing";
import { getAllServicePinTagsAction } from "../../redux/actions/pintagAction";

export interface IPosDashboardPageProps {}

const PosDashboardPage: FC<IPosDashboardPageProps> = () => {
  const [draftConfirmationDialogOpen, setDraftConfirmationDialog] =
    useState<boolean>(false);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );
  const { drafts, cart, billById, getCashierDrafts, selectedSession } =
    useAppSelector((state) => state.cart);
  const { user } = useAppSelector((state) => state.auth);
  const [isHydrating, setIsHydrating] = useState(true);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { customer } = useAppSelector((state) => state.customer);
  const { showSnackbar } = useSnackbarContext();
  const [draftIds, setDraftIds] = useState<number[]>([]);
  const initialEmptyIdsRef = useRef<string[] | null>(null);

  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { categoryList } = usePricing();
  const customerId = params.id;
  const billId = params.billId;
  const duplicateFromBillId = searchParams.get("duplicateFrom");
  const draftsAutoLoadedRef = useRef(false);
  const autoDeleteAfterLoad = true;

  const mergeSessions = (sessions: any[] = []) => {
    const byId = new Map<string, any>();
    for (const s of sessions) {
      if (!s || !s.sessionId) continue;
      if (byId.has(s.sessionId)) {
        const prev = byId.get(s.sessionId);
        byId.set(s.sessionId, {
          ...prev,
          cartItems: [...(prev.cartItems ?? []), ...(s.cartItems ?? [])],
        });
      } else {
        byId.set(s.sessionId, s);
      }
    }
    return Array.from(byId.values());
  };

  useEffect(() => {
    if (billId || duplicateFromBillId) return;

    const run = async () => {
      if (draftsAutoLoadedRef.current) return;
      if (!customerId || isNaN(Number(customerId))) return;
      if (!user?.id) return;

      const payload = await dispatch(getCashierDraftsAction(user.id)).unwrap();
      const mine = (payload ?? []).filter(
        (d: any) => d?.customer?.id === Number(customerId)
      );
      if (!mine.length) return;

      const mergedSessions = mergeSessions(
        mine.flatMap((d: any) => d.draftSessions ?? [])
      );

      dispatch(CartSliceAction.clearSession());

      dispatch(CartSliceAction.setSelectedCustomer(mine[0].customer));
      dispatch(CartSliceAction.setCartFromDraft(mergedSessions));
      dispatch(CartSliceAction.pruneEmptySessions());

      if (autoDeleteAfterLoad) {
        for (const d of mine) {
          try {
            await dispatch(deleteDraftAction(d.draftId)).unwrap();
          } catch {}
        }
        dispatch(getCashierDraftsAction(user.id));
      }

      draftsAutoLoadedRef.current = true;
    };

    run();
  }, [billId, duplicateFromBillId, customerId, dispatch, user?.id]);

  useEffect(() => {
    dispatch(CartSliceAction.pruneEmptySessions());
  }, []);

  // useEffect(() => {
  //   dispatch(getCashierDraftsAction(4)).unwrap();
  // }, []);

  // useEffect(() => {
  //   if (getCashierDrafts?.length) {
  //     const ids = getCashierDrafts.map((draft) => draft.id);
  //     setDraftIds(ids);
  //   }
  // }, [getCashierDrafts]);

  // const handleDiscardAllDrafts = async () => {
  //   for (const id of draftIds) {
  //     try {
  //       await dispatch(deleteDraftAction(id)).unwrap();
  //     } catch (err) {
  //       console.error(`Failed to delete draft ${id}`, err);
  //     }
  //   }
  // };

  const fetchBillId = async (id: string) => {
    try {
      const res = await dispatch(
        getBillByIdAction({
          billId: id,
        })
      ).unwrap();
      if (res) {
        const bill = res;
        if (bill) {
          const savedSession = JSON.parse(bill.billString);
          // A Special ticket's totals keep its Special charge while editing
          savedSession.isSpecial = !!bill.pickupDetails?.isSpecial;
          dispatch(CartSliceAction.clearSession());
          dispatch(CartSliceAction.setSelectedCustomer(bill.customerId));
          dispatch(addSession(savedSession));
          // Items can only grow while the ticket's pin tags cover them
          // (3.m/3.n): use its current tags, else the ones saved at check-in.
          // Items per tag come from the pin tag rules, so load them here too
          dispatch(getAllServicePinTagsAction());
          const startingTags: string[] =
            bill.pintagDetails?.tags ?? savedSession.pinTag?.tags ?? [];
          dispatch(
            CartSliceAction.setEditPinTagCapacity({
              sessionId: savedSession.sessionId,
              tags: startingTags.filter((tag) => !!tag && String(tag).trim()).length,
            })
          );
          // Paid invoices keep the discount they were saved with; unpaid
          // ones (and uncleared Pay Later / Monthly ones) start from it and
          // can change it, as at check-in.
          const editDiscount = editDiscountState(
            bill.paidAmount,
            discountFromSavedBill(
              savedSession.discount,
              categoryList?.body.map((cat) => cat.id) ?? []
            ),
            bill.discountLockReason
          );
          if (editDiscount.locked) {
            dispatch(
              DiscountSliceAction.lockDiscount({
                discount: editDiscount.discount,
                reason: editDiscount.reason,
              })
            );
          } else if (editDiscount.custom) {
            dispatch(DiscountSliceAction.setCustomDiscount(editDiscount.custom));
          } else if (editDiscount.selectedId) {
            dispatch(DiscountSliceAction.setSelectedDiscount(editDiscount.selectedId));
          }
          // Edit Invoice needs per-item pickup status so already-picked-up
          // items can be locked from further editing (CartRow reads this
          // via state.cart.billItemIdList, matched by bill.id).
          dispatch(getBillItemIdListAction(bill.id));
          const serviceType = categoryList?.body.find(
            (item) => item.id === Number(bill.serviceTypeId)
          );
          serviceType &&
            dispatch(CartSliceAction.setSelectedServiceType(serviceType));
        } else {
          showSnackbar("Bill not found", "error");
          navigate(-1);
        }
      } else {
        showSnackbar("Bill not found", "error");
        navigate(-1);
      }
    } catch (error) {
      showSnackbar("Bill fetch error", "error");
      navigate(-1);
    }
  };

  const fetchDuplicateBillData = async (id: string) => {
    try {
      const res = await dispatch(
        getBillByIdAction({
          billId: id,
        })
      ).unwrap();
      if (res) {
        const bill = res;
        const parsedSession = JSON.parse(bill.billString) as ICartSession;
        // A copy starts as Regular; its pin tag decides at check-in
        const duplicatedSession: ICartSession = {
          ...parsedSession,
          sessionId: uuidv4(),
          isSpecial: false,
          cartItems: (parsedSession.cartItems ?? []).map((item) => ({
            ...item,
            itemId: uuidv4(),
            specialCharge: undefined,
          })),
        };
        dispatch(CartSliceAction.clearSession());
        dispatch(CartSliceAction.setSelectedCustomer(bill.customerId));
        dispatch(addSession(duplicatedSession));
        const serviceType = categoryList?.body.find(
          (item) => item.id === Number(bill.serviceTypeId)
        );
        serviceType &&
          dispatch(CartSliceAction.setSelectedServiceType(serviceType));
        navigate(location.pathname, { replace: true });
      } else {
        showSnackbar("Bill not found", "error");
        navigate(-1);
      }
    } catch (error) {
      showSnackbar("Bill fetch error", "error");
      navigate(-1);
    }
  };

  const fetchUpdatingBillData = async () => {
    if (billId) {
      if (Number(billId) > 0 || !isNaN(Number(billId))) {
        if (
          customerId &&
          customerId !== "undefined" &&
          customerId !== "null" &&
          Number(customerId) > 0 &&
          !isNaN(Number(customerId))
        ) {
          const res = await dispatch(
            getCustomerByIdAction(customerId)
          ).unwrap();
          if (res && res.id) {
            dispatch(CartSliceAction.setSelectedCustomer(res));
            fetchBillId(billId);
          } else {
            showSnackbar("Customer not found", "error");
            navigate(-1);
          }
        } else {
          showSnackbar("Invalid customer ID", "error");
          navigate(-1);
        }
      } else {
        showSnackbar("Invalid bill ID", "error");
        navigate(-1);
      }
    }
  };
  useEffect(() => {
    if (billId) {
      fetchUpdatingBillData();
    }
  }, [billId]);

  useEffect(() => {
    if (duplicateFromBillId) {
      fetchDuplicateBillData(duplicateFromBillId);
    }
  }, [duplicateFromBillId]);

  const handleOnClickTopRight = () => {
    let isNonEmptySession = false;
    cart.forEach((session) => {
      if (session.cartItems?.filter((item) => item.finalized).length > 0) {
        isNonEmptySession = true;
        return;
      } else {
        isNonEmptySession = false;
      }
    });
    if (isNonEmptySession) {
      setPendingNavigation("/pos/lobby");
      setDraftConfirmationDialog(true);
    } else {
      navigate("/pos/lobby");
    }
    // setDraftConfirmationDialog(true);
  };

  const hasUnsavedChanges = cart.some(
    (session) => session.cartItems?.length > 0
  );

  const blocker = useBlocker(({ nextLocation }) => {
    if (hasUnsavedChanges && !allowNavigation) {
      setPendingNavigation(nextLocation.pathname);
      setDraftConfirmationDialog(true);
      return true;
    }
    return false;
  });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleConfirmExit = () => {
    setAllowNavigation(true);
    if (pendingNavigation) {
      const targetPage = pendingNavigation;
      setPendingNavigation(null);
      navigate(targetPage, { replace: true });
    } else {
      navigate("/pos/lobby");
    }
    setDraftConfirmationDialog(false);
  };

  const handleCancelExit = () => {
    setDraftConfirmationDialog(false);
    setPendingNavigation(null);
  };

  useEffect(() => {
    return () => {
      dispatch(CustomerSliceAction.clearCustomer());
      dispatch(CustomerSliceAction.clearCustomerNotes());
      dispatch(CartSliceAction.clearBill());
    };
  }, []);

  // A discount belongs to one order: drop it when the cashier leaves this
  // check-in (or switches customer / bill) so it can't carry over.
  useEffect(() => {
    return () => {
      dispatch(DiscountSliceAction.resetDiscount());
    };
  }, [customerId, billId]);

  useEffect(() => {
    if (customerId) {
      dispatch(getCustomerByIdAction(customerId));
    }
  }, [customerId]);

  useEffect(() => {
    if (allowNavigation && pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [allowNavigation, pendingNavigation]);

  const handleOnDraftConfirm = async () => {
    if (!customer) {
      showSnackbar("Customer not found", "error");
      return;
    }

    if (!user?.storeId) {
      showSnackbar(
        "Your account isn't assigned to a store. Contact your administrator before saving a draft.",
        "error"
      );
      return;
    }

    let finalizedCart = cart
      .map((session) => ({
        ...session,
        cartItems: session.cartItems?.filter((item) => item.finalized),
      }))
      .filter((session) => session.cartItems?.length);

    const finalizedDraft: IDraft = {
      draftId: drafts.length + 1,
      customer: customer,
      draftSessions: finalizedCart,
      createdAt: new Date(),
      totalPieces: finalizedCart.reduce(
        (acc, session) =>
          acc +
          session.cartItems!.reduce(
            (acc, item) => acc + item.qty * (item.garmentStyle?.pieceCount ?? 1),
            0
          ),
        0
      ),
      totalAmount: finalizedCart.reduce(
        (acc, session) =>
          acc +
          session.cartItems!.reduce(
            (acc, item) => acc + item.qty * item.unitPrice,
            0
          ),
        0
      ),
    };

    try {
      await dispatch(
        saveDraftAction({
          tenantId: 1,
          storeId: user.storeId,
          cashierId: user.id,
          customerId: customer.id,
          bills: finalizedDraft,
        })
      ).unwrap();
      await dispatch(CartSliceAction.setDrafts([...drafts, finalizedDraft]));

      setAllowNavigation(true);

      if (pendingNavigation) {
        const targetPage = pendingNavigation;
        setPendingNavigation(null);
        navigate(targetPage, { replace: true });
      }

      setDraftConfirmationDialog(false);
    } catch (err) {
      showSnackbar("Failed to save draft. Please try again.", "error");
    }
  };

  useEffect(() => {
    const cartlength = cart.length;
    if (cartlength > 1 && cart[cartlength - 1].cartItems.length === 0) {
      if (isHydrating == true) {
        dispatch(removeSession());
        setIsHydrating(false);
      }
    }
  });

  return (
    <PosLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          height: "100%",
          gap: "0.57rem",
        }}
      >
        <ExitConfirmationModal
          open={draftConfirmationDialogOpen}
          title="Exit Confirmation"
          message="Do you want to save customer sessions as a draft or discard?"
          saveButtonText="Save and exit"
          discardButton={{
            text: "Discard and Exit",

            onClick: async () => {
              // await handleDiscardAllDrafts();
              setAllowNavigation(true);
              if (pendingNavigation) {
                const targetPage = pendingNavigation;
                setPendingNavigation(null);
                navigate(targetPage, { replace: true });
              }
              setDraftConfirmationDialog(false);
            },
          }}
          onCancel={handleCancelExit}
          onConfirm={async () => {
            await handleOnDraftConfirm();
            setDraftConfirmationDialog(false);
          }}
        />
        <InfoSection
          mode={PosInfoSectionMode.CART}
          editingBillId={(billId && billById?.billId) ?? undefined}
        />
        <Box
          sx={{ width: "0.142857143rem", background: Colors.ACCENT_GRAY }}
        ></Box>
        <ServicesSection />
        <TopRightButton variation="Back" onClick={handleOnClickTopRight} />
      </Box>
    </PosLayout>
  );
};

export default PosDashboardPage;
